import React, { createContext, useContext, useState, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark' | 'auto';

export interface AppThemeSettings {
  mode: ThemeMode;
  primaryColor: string;
}

interface ThemeContextValue {
  themeSettings: AppThemeSettings;
  setThemeSettings: (s: AppThemeSettings) => void;
  resolvedMode: 'light' | 'dark';
}

const STORAGE_KEY = 'admin_theme_v1';

const DEFAULT_THEME: AppThemeSettings = { mode: 'light', primaryColor: 'blue' };

export const COLOR_MAP: Record<string, string> = {
  blue:   '#1976d2',
  green:  '#2e7d32',
  orange: '#e65100',
  purple: '#7b1fa2',
};

export function resolveColor(color: string): string {
  return COLOR_MAP[color] ?? color;
}

function getSystemDark(): boolean {
  return typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function load(): AppThemeSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_THEME, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_THEME;
}

const ThemeContext = createContext<ThemeContextValue>({
  themeSettings: DEFAULT_THEME,
  setThemeSettings: () => {},
  resolvedMode: 'light',
});

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeSettings, setThemeSettingsState] = useState<AppThemeSettings>(load);

  const setThemeSettings = useCallback((s: AppThemeSettings) => {
    setThemeSettingsState(s);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
  }, []);

  const resolvedMode: 'light' | 'dark' =
    themeSettings.mode === 'auto'
      ? (getSystemDark() ? 'dark' : 'light')
      : themeSettings.mode;

  return (
    <ThemeContext.Provider value={{ themeSettings, setThemeSettings, resolvedMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeSettings = () => useContext(ThemeContext);
